import { useState, useEffect } from 'react'
import _ from 'lodash'
import { useErrorHandler } from 'react-error-boundary'
import { FormLoader, Toast, ProposalSection } from '@vzmi/shp_shared_compo'
import { PROPOSAL_REVIEW_STATUS } from '@vzmi/shp_shared_compo/constants/proposal'
import { withCommonPageErrorBoundary } from '../../Common/HOC/withErrorBoundary'
import { withI13nWrapper } from '../../Common/I13nWrapper'
import withUpdateListingForm from '../../Proposal/Common/HOC/withUpdateListingForm'
import { FRONT_END_DEFINED_ERROR_CODE } from '../../../constants/globalTypes'
import { PROPOSAL_TYPE_NAME } from '../../../constants/proposalTypes'
import { APPLY_ADD_OPTIONS_TYPE, APPLY_FIELD_SCOPE, APPLY_FIELD } from '../../../constants/batchProposals'
import ProposalConsent from '../../Proposal/Common/ProposalConsent/ProposalConsent'
import ProposalCompletion from '../../Proposal/Common/ProposalCompletion/ProposalCompletion'
import { getFormValues } from '../../Proposal/Common/ProposalForm/Shared/util'
import ContentLayout from '../Common/ContentLayout'
import ActionHeader from '../Common/ActionHeader'
import ActionFooter from '../Common/ActionFooter'
import Background from '../Common/Background'
import BackToListingQueryLink from '../Common/BackToListingQueryLink'
import UpdateFailedListingsCard from '../Common/Card/UpdateFailedListings'
import UpdatedListingsTableCard from '../Common/Card/UpdatedListingsTable'
import NotFoundListingsSection from '../Common/Section/NotFoundListings'
import PreSubmitModalContent from '../Common/PreSubmitModalContent'
import UpdateFailedListingsSection from '../Common/Section/UpdateFailedListings'
import batchProposals from '../request/batchProposals'
import useBeforeunload from '../hook/useBeforeunload'
import usePageFlow from '../hook/usePageFlow'
import useI13N from '../hook/useI13N'
import useBackgroundFormDataOnInit from '../hook/useBackgroundFormDataOnInit'
import useListingsOnComposeInit from '../hook/useListingsOnComposeInit'
import useProposalsOnOverviewInit from '../hook/useProposalsOnOverviewInit'
import { useQueryString } from '@vzmi/shp_shared_compo/hook/useQueryString'
import useUIState from '../hook/useUIState'
import useListingState from '../hook/useListingState'
import { FlowContext } from '../helper/flow'
import {
  getBatchResult,
  getListingsWithProposal,
  getCombinedBatchResult,
  getDuplicatedListingsGroupByListingId
} from '../helper/batch'
import {
  getFormStateFromStorage,
  clearStateFromStorage
} from '../helper/storage'
import {
  getParamsList,
  getConvertedListings,
  getConvertedListingsAndModifiedList
} from './helper'
import CopyAndWarrantyRows from './CopyAndWarrantyRows'
import BatchApplyRows from './BatchApplyRows'
import styles from './index.css'
import { findFormErrorElements, scrollToErrorElement } from '../../../lib/FormHelper'

const DEFAULT_LISTINGS_FIELDS = [
  '-videos',
  '-productStructuredDataList'
]

const UpdateCopy = props => {
  const {
    match: { params: { phase } },
    dispatch,
    change,
    initialize,
    actions,
    flags,
    user,
    proposalType,
    reduxFormState,
    i13nMethods,
    i13n
  } = props

  const { state: pageState, actions: pageActions } = useUIState(props)
  const { state: listingState, actions: listingActions } = useListingState()
  const {
    listings,
    deletedListings,
    queryStringListingIds,
    batchResult
  } = listingState
  const { successProposalMap, failedProposalMap } = batchResult
  const pageFlow = usePageFlow(phase, pageState.pageFlow)
  const {
    isEditPage,
    isConfirmPage,
    isSuccessPage,
    isOverviewPage,
    readOnly
  } = pageFlow

  const currentQueryStringListingIds = useQueryString(props, 'listingIds', true)

  // 在修改、確認、完成頁加上 GA 紀錄
  const i13nData = {
    proposalType,
    queryStringListingIds: currentQueryStringListingIds
  }
  useI13N(i13nMethods, i13nData, pageFlow)

  // 修改頁綁上離開頁面確認事件
  useBeforeunload(isOverviewPage)

  // 頁面初始化寫入賣場背景資料
  useBackgroundFormDataOnInit(props, initialize)

  const [copyApplyBtnStatus, setCopyApplyBtnStatus] = useState(true)

  useEffect(() => {
    const formValues = getFormValues(reduxFormState)
    const [copy, operates] = _.flow(
      () => [APPLY_FIELD.COPY, APPLY_FIELD.OPERATES],
      fields => _.map(fields, field => _.get(formValues, field))
    )()
    // 詳情編輯器為空或為選擇「動作」，則 disable 不可點擊
    const isDisabled = (_.isEmpty(copy) || _.isEmpty(operates))
    setCopyApplyBtnStatus(isDisabled)
  }, [reduxFormState])

  const [
    duplicatedMap,
    setDuplicatedMap
  ] = useState({})
  const listingsParams = {
    id: currentQueryStringListingIds,
    fields: DEFAULT_LISTINGS_FIELDS
  }
  const handleError = useErrorHandler()
  // 修改頁初始化取得批次賣場資料
  useListingsOnComposeInit(props, listingsParams, isEditPage,
    () => pageActions.setIsLoading(true),
    (error, fetchedListings) => {
      if (error) {
        pageActions.setIsLoading(false)
        Toast.error(error.message)
        return
      }

      // 查無賣場
      if (_.isEmpty(fetchedListings)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_LISTING_NOT_FOUND })
        return
      }

      const savedFormListings = _.get(getFormStateFromStorage(), 'listings')
      const currentListings = (
        // 如果 okta 逾時重新取得 token 回來頁面會直接使用 localstorage 儲存的賣場修改狀態
        savedFormListings ||
        fetchedListings
      )
      const currentDuplicatedMap = getDuplicatedListingsGroupByListingId(currentListings)
      const convertedListings = getConvertedListings(currentListings)
      pageActions.setIsLoading(false)
      listingActions.setListings(convertedListings)
      listingActions.setQueryStringListingIds(currentQueryStringListingIds)
      setDuplicatedMap(currentDuplicatedMap)
      changeFormListings(convertedListings)
      change(APPLY_FIELD.OPTIONS, APPLY_ADD_OPTIONS_TYPE.TOP, '')
      clearStateFromStorage()
    }
  )

  const currentQueryStringProposalIds = useQueryString(props, 'ids', true)
  const proposalsParams = {
    proposal: {
      id: currentQueryStringProposalIds,
      fields: '-struData,-product.copy'
    },
    listing: {
      fields: DEFAULT_LISTINGS_FIELDS
    }
  }
  // 檢視頁初始化取得批次提案資料
  useProposalsOnOverviewInit(props, proposalsParams, isOverviewPage,
    () => pageActions.setIsLoading(true),
    (error, currentProposals, currentListings) => {
      if (error) {
        pageActions.setIsLoading(false)
        Toast.error(error.message)
        return
      }

      // 查無提案
      if (_.isEmpty(currentProposals)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_PROPOSAL_NOT_FOUND })
        return
      }

      // 查無賣場
      if (_.isEmpty(currentListings)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_LISTING_NOT_FOUND })
        return
      }

      const currentBatchResult = getBatchResult(currentProposals.map(proposal => ([undefined, proposal])))
      const covertedListings = _.flow(
        () => getListingsWithProposal(currentListings, currentBatchResult),
        listings => getConvertedListings(listings)
      )()
      pageActions.setIsLoading(false)
      listingActions.setListings(covertedListings)
      listingActions.setBatchResult(currentBatchResult)
      changeFormListings(covertedListings)
    }
  )

  // 修改 redux form 裡的賣場
  const changeFormListings = listings => change('listings', listings)

  // 完成提案
  const onComplete = async () => {
    const formValues = getFormValues(reduxFormState)
    const formListings = _.get(formValues, 'listings', [])
    const formListingMapById = _.keyBy(formListings, 'id')
    const paramsList = getParamsList(
      formValues,
      PROPOSAL_REVIEW_STATUS.DRAFT,
      duplicatedMap
    )
    const proposals = await batchProposals(actions, paramsList)
    const combinedBatchResult = getCombinedBatchResult(
      batchResult,
      getBatchResult(proposals)
    )
    const { successProposalMap } = combinedBatchResult
    const covertedListings = _.flow(
      // 完成提案後用含有提案編號的資料取代原本的賣場資料
      // 送出提案、返回修改再次完成提案時才有 proposalId 可以使用
      () => getListingsWithProposal(formListings, combinedBatchResult),
      // 將送出前"同時修改商品保證"狀態保留下來
      listingsWithProposal => _.map(listingsWithProposal, (listing) => {
        return {
          ...listing,
          isUpdateWithWarranty: _.get(formListingMapById, `${listing.id}.isUpdateWithWarranty`)
        }
      }),
      // 從 api 取得的賣場資料要再轉換成前端格式給 component 使用
      getConvertedListings
    )()
    const updatedListings = _.filter(covertedListings, listing => {
      return (listing.id in successProposalMap)
    })
    listingActions.setListings(covertedListings)
    listingActions.setBatchResult(combinedBatchResult)
    changeFormListings(covertedListings)
    return [null, updatedListings]
  }

  // 送出提案
  const onPendingReview = async () => {
    const formValues = getFormValues(reduxFormState)
    const { failedProposalMap } = batchResult
    const paramsList = _.filter(
      getParamsList(
        formValues,
        PROPOSAL_REVIEW_STATUS.PENDING_REVIEW,
        duplicatedMap
      ),
      // 送出提案過濾掉完成提案時失敗的提案
      params => !(_.get(params, 'listing.id') in failedProposalMap)
    )
    const proposals = await batchProposals(actions, paramsList)
    const combinedBatchResult = getCombinedBatchResult(
      batchResult,
      getBatchResult(proposals)
    )
    const { successProposalMap } = combinedBatchResult
    const covertedListings = _.flow(
      () => getListingsWithProposal(listings, combinedBatchResult),
      getConvertedListings
    )()
    const updatedListings = _.filter(covertedListings, listing => {
      return (listing.id in successProposalMap)
    })
    listingActions.setListings(covertedListings)
    listingActions.setBatchResult(combinedBatchResult)
    changeFormListings(covertedListings)
    return [null, updatedListings]
  }

  // 下次再改按鈕觸發刪除該賣場於 state 跟 redux form 的資料
  // 並重新計算可能含有共用商品的賣場
  const onRemoveListing = async (id, listings) => {
    const newListings = []
    const deletedListings = []
    for (const listing of listings) {
      if (listing.id === id) {
        deletedListings.push(listing)
      } else {
        newListings.push(listing)
      }
    }
    const currentDuplicatedMap = getDuplicatedListingsGroupByListingId(newListings)
    // state 紀錄被刪除的賣場、更新既有賣場
    listingActions.addDeletedListings(deletedListings)
    // redux form 更新賣場欄位
    changeFormListings(newListings)
    // state 重新計算含有共用商品的賣場
    setDuplicatedMap(currentDuplicatedMap)
  }

  // 套用至所有賣場按鈕
  const onUpdateWarranty = (listing) => {
    const { warranty, isUpdateWithWarranty } = listing
    const formValues = getFormValues(reduxFormState)
    const formListings = _.get(formValues, 'listings', []) // 取得當前redux from Data
    const newListings = _.map(formListings, item => { // 所有賣場 warranty 更換成點擊的該賣場 warranty
      return { ...item, warranty, isUpdateWithWarranty }
    })
    changeFormListings(newListings)
  }

  // 套用詳情至所有賣場
  const onCopyApply = async () => {
    const applyAreaSelector = '[data-role="batch-apply-rows"]'
    const errorEles = await findFormErrorElements(applyAreaSelector)
    if (!_.isEmpty(errorEles)) {
      await scrollToErrorElement((window.innerHeight / 2) * -1, applyAreaSelector)
      return
    }

    const formValues = getFormValues(reduxFormState)
    const formApplyDetails = _.get(formValues, APPLY_FIELD_SCOPE, {})
    const formListings = _.get(formValues, 'listings', [])
    // 轉換所有套用後賣場資料 及 判斷套用後彈跳訊息資料
    const list = getConvertedListingsAndModifiedList(formListings, formApplyDetails)
    changeFormListings(list.newListings)
    getMessageContent(list.modifiedList)
  }

  const getMessageContent = (modifiedList) => {
    // 新增要略過
    if (modifiedList.length === 0) {
      Toast.success('套用成功')
    } else {
      const replacedIsTrueList = _.filter(modifiedList, { replaced: true })
      if (replacedIsTrueList.length === modifiedList.length) {
        Toast.success('套用成功')
      } else if (replacedIsTrueList.length === 0) {
        Toast.info('無賣場比對到完全符合之內容')
      } else {
        const notReplacedIds = _.map(_.filter(modifiedList, { replaced: false }), 'id').join(',')
        Toast.info(
          <div className={styles.info}>
            {`賣場 ${notReplacedIds} 未比對到完全符合之內容，已自動略過，其他賣場移除成功`}
          </div>
        )
      }
    }
  }

  // header footer context
  // 避免在主要表單中使用此 context 避免重複 render component
  const flowContextValue = {
    phase,
    pageFlow: pageState.pageFlow,
    isLoading: pageState.isLoading,
    isPending: pageState.isPending,
    setPageFlow: pageActions.setPageFlow,
    setIsLoading: pageActions.setIsLoading,
    setIsPending: pageActions.setIsPending
  }
  const proposalTypeName = `批次${PROPOSAL_TYPE_NAME[proposalType]}`
  const completeName = `${proposalTypeName}已送出`

  if (_.isEmpty(listings)) {
    return (
      <FormLoader
        isLoading
        fixed
      />
    )
  }

  return (
    <ContentLayout isSuccessPage={isSuccessPage}>
      <FormLoader
        isLoading={pageState.isLoading}
        fixed
      >
        {
          // 完成頁
          isSuccessPage && (
            <>
              <ProposalCompletion
                title={completeName}
                proposalType={proposalType}
                withHint={false}
              />
              <UpdateFailedListingsCard
                proposalTypeName={proposalTypeName}
                listings={listings}
                failedMap={failedProposalMap}
              />
              <UpdatedListingsTableCard
                proposalType={proposalType}
                listings={listings}
                successMap={successProposalMap}
              />
            </>
          )
        }
        {
          // 修改、確認、檢視頁
          !isSuccessPage && (
            <>
              <FlowContext.Provider value={flowContextValue}>
                <ActionHeader
                  title={proposalTypeName}
                  listingIds={queryStringListingIds}
                />
              </FlowContext.Provider>
              <Background
                flags={flags}
                user={user}
                isEditPage={isEditPage}
                readOnly={readOnly}
              />
              {isConfirmPage && <ProposalConsent />}
              <NotFoundListingsSection
                listingIdsFromUrl={queryStringListingIds}
                listings={listings}
                deletedListings={deletedListings}
                isVisible={isEditPage}
              />
              <UpdateFailedListingsSection
                proposalTypeName={proposalTypeName}
                listings={listings}
                failedMap={failedProposalMap}
              />
              <ProposalSection
                title='詳情與保證'
                data-role='batch-update-copy'
              >
                {isEditPage && (
                  <BatchApplyRows
                    readOnly={readOnly}
                    disabled={copyApplyBtnStatus}
                    onUpdate={onCopyApply}
                  />
                )}
                <CopyAndWarrantyRows
                  readOnly={readOnly}
                  proposalType={proposalType}
                  onRemove={onRemoveListing}
                  batchResult={batchResult}
                  duplicatedMap={duplicatedMap}
                  isOverviewPage={isOverviewPage}
                  flags={flags}
                  user={user}
                  change={change}
                  onUpdate={onUpdateWarranty}
                  i13n={i13n}
                  isEditPage={isEditPage}
                />
              </ProposalSection>
              <FlowContext.Provider value={flowContextValue}>
                <ActionFooter
                  dispatch={dispatch}
                  batchResult={batchResult}
                  onComplete={onComplete}
                  onPendingReview={onPendingReview}
                  preSubmitContent={(
                    <PreSubmitModalContent
                      duplicatedMap={duplicatedMap}
                    />
                  )}
                  withPreSubmit={!_.isEmpty(duplicatedMap)}
                />
              </FlowContext.Provider>
            </>
          )
        }
      </FormLoader>
    </ContentLayout>
  )
}

const hoc = _.flow([
  withI13nWrapper,
  // common page: yahoo title & supplier part
  // error boundary
  withCommonPageErrorBoundary(<BackToListingQueryLink />),
  // proposal redux form init
  withUpdateListingForm
])

export default hoc(UpdateCopy)
